import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ChevronLeft, ChevronRight, Send, BookOpen, User, CheckCircle, Shield, Clock, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default exam duration in seconds (90 minutes) - will be overridden by settings
const DEFAULT_EXAM_DURATION = 90 * 60;

export default function ExamPage() {
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [currentSection, setCurrentSection] = useState("section1");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [subjectiveAnswers, setSubjectiveAnswers] = useState({
    41: { selectedOption: 1, answer: "" },
    42: { selectedOption: 1, answer: "" }
  });
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examDuration, setExamDuration] = useState(DEFAULT_EXAM_DURATION);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_EXAM_DURATION);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const hasAutoSubmitted = useRef(false);
  // Refs to always hold the latest answers (avoids stale-closure bug when timer auto-submits)
  const mcqAnswersRef = useRef({});
  const subjectiveAnswersRef = useRef({});
  const candidateRef = useRef(null);
  const handleAutoSubmitRef = useRef(null);

  // Fetch exam settings on mount
  useEffect(() => {
    const fetchExamSettings = async () => {
      try {
        const response = await axios.get(`${API}/admin/exam-settings`);
        const durationInSeconds = (response.data.exam_duration_minutes || 90) * 60;
        setExamDuration(durationInSeconds);
        // Only set timeLeft if we haven't loaded saved time yet
        const savedTime = localStorage.getItem(`examTimer_${JSON.parse(localStorage.getItem("candidate") || "{}").id}`);
        if (!savedTime) {
          setTimeLeft(durationInSeconds);
        }
      } catch (error) {
        console.log("Using default exam duration");
      }
    };
    fetchExamSettings();
  }, []);

  useEffect(() => {
    const storedCandidate = localStorage.getItem("candidate");
    if (!storedCandidate) {
      toast.error("Please register first");
      navigate("/");
      return;
    }
    const candidateData = JSON.parse(storedCandidate);
    setCandidate(candidateData);
    fetchQuestions(candidateData.id);
    
    // Load saved time if exists
    const savedTime = localStorage.getItem(`examTimer_${candidateData.id}`);
    if (savedTime) {
      const parsedTime = parseInt(savedTime);
      if (parsedTime > 0) {
        setTimeLeft(parsedTime);
      }
    }
  }, [navigate]);

  // Timer countdown effect
  useEffect(() => {
    if (loading || showSuccessDialog) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;
        
        // Save time to localStorage every 10 seconds
        if (candidate && newTime % 10 === 0) {
          localStorage.setItem(`examTimer_${candidate.id}`, newTime.toString());
        }
        
        // Show warning when 5 minutes left
        if (newTime === 300 && !showTimeWarning) {
          setShowTimeWarning(true);
          toast.warning("5 minutes remaining!", { duration: 5000 });
        }
        
        // Auto-submit when time runs out
        if (newTime <= 0 && !hasAutoSubmitted.current) {
          hasAutoSubmitted.current = true;
          clearInterval(timerRef.current);
          // Call via ref so we always invoke the LATEST handleAutoSubmit
          // (the latest version reads the current answers from refs)
          if (handleAutoSubmitRef.current) {
            handleAutoSubmitRef.current();
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, showSuccessDialog, candidate]);

  // Auto-save answers every 30 seconds
  useEffect(() => {
    if (loading || showSuccessDialog || !candidate) return;
    
    autoSaveRef.current = setInterval(() => {
      autoSaveAnswers();
    }, 30000); // Auto-save every 30 seconds
    
    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
    };
  }, [loading, showSuccessDialog, candidate, mcqAnswers, subjectiveAnswers]);

  // Auto-save answers to localStorage AND to the server (every ~30s)
  const autoSaveAnswers = useCallback(async () => {
    if (!candidate) return;

    // Read from refs so we always save the freshest values, not stale closures
    const latestMcq = mcqAnswersRef.current || {};
    const latestSubjective = subjectiveAnswersRef.current || {};

    setAutoSaving(true);

    // 1) LocalStorage (browser-side, instant)
    try {
      const savedData = {
        mcqAnswers: latestMcq,
        subjectiveAnswers: latestSubjective,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(`examAnswers_${candidate.id}`, JSON.stringify(savedData));
    } catch (error) {
      console.error("LocalStorage auto-save failed:", error);
    }

    // 2) Server-side auto-save (canonical safety net against browser/network loss)
    try {
      const mcqList = Object.entries(latestMcq).map(([id, answer]) => ({
        question_id: parseInt(id),
        answer
      }));
      const subjList = Object.entries(latestSubjective).map(([id, data]) => ({
        question_id: parseInt(id),
        selected_option: data.selectedOption,
        answer: data.answer || ""
      }));
      await axios.put(`${API}/candidate/auto-save/${candidate.id}`, {
        candidate_id: candidate.id,
        mcq_answers: mcqList,
        subjective_answers: subjList
      }, { timeout: 8000 });
      setLastAutoSave(new Date());
      console.log(`Server auto-save OK at ${new Date().toLocaleTimeString()} — ${mcqList.length} MCQs`);
    } catch (error) {
      // Non-fatal: localStorage still has the data; next tick will retry
      console.warn("Server auto-save failed (will retry):", error?.message);
    } finally {
      setAutoSaving(false);
    }
  }, [candidate]);

  // Load saved answers on mount — SERVER draft takes precedence over local
  useEffect(() => {
    if (!candidate) return;

    const restore = async () => {
      let restored = false;

      // 1) Try server-side draft first (authoritative, survives browser/device change)
      try {
        const res = await axios.get(`${API}/candidate/draft/${candidate.id}`, { timeout: 8000 });
        if (res.data?.has_draft) {
          const mcqObj = {};
          (res.data.mcq_answers || []).forEach(a => { mcqObj[a.question_id] = a.answer; });
          const subjObj = {};
          (res.data.subjective_answers || []).forEach(a => {
            subjObj[a.question_id] = { selectedOption: a.selected_option || 1, answer: a.answer || "" };
          });
          if (Object.keys(mcqObj).length || Object.keys(subjObj).length) {
            setMcqAnswers(mcqObj);
            mcqAnswersRef.current = mcqObj;
            setSubjectiveAnswers(prev => ({ ...prev, ...subjObj }));
            subjectiveAnswersRef.current = { ...(subjectiveAnswersRef.current || {}), ...subjObj };
            toast.info("Previous answers restored from server", { duration: 3000 });
            restored = true;
          }
        }
      } catch (e) {
        console.warn("Server draft fetch failed, falling back to local:", e?.message);
      }

      // 2) Fallback to localStorage
      if (!restored) {
        const savedAnswers = localStorage.getItem(`examAnswers_${candidate.id}`);
        if (savedAnswers) {
          try {
            const parsed = JSON.parse(savedAnswers);
            if (parsed.mcqAnswers) {
              setMcqAnswers(parsed.mcqAnswers);
              mcqAnswersRef.current = parsed.mcqAnswers;
            }
            if (parsed.subjectiveAnswers) {
              setSubjectiveAnswers(parsed.subjectiveAnswers);
              subjectiveAnswersRef.current = parsed.subjectiveAnswers;
            }
            toast.info("Previous answers restored", { duration: 3000 });
          } catch (error) {
            console.error("Failed to restore saved answers:", error);
          }
        }
      }
    };
    restore();
  }, [candidate]);

  const handleAutoSubmit = useCallback(async () => {
    // Always read from refs to guarantee the latest answers are submitted,
    // even if the timer closure was created before recent typing
    const currentCandidate = candidateRef.current || candidate;
    if (!currentCandidate) return;

    // Pull latest answers from refs (set on every keystroke / click)
    let latestMcq = mcqAnswersRef.current || {};
    let latestSubjective = subjectiveAnswersRef.current || {};

    // Defensive fallback: if refs are empty (e.g., first-render edge case), try localStorage
    if (Object.keys(latestMcq).length === 0 && Object.keys(latestSubjective).length === 0) {
      try {
        const cached = localStorage.getItem(`examAnswers_${currentCandidate.id}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.mcqAnswers && Object.keys(parsed.mcqAnswers).length) latestMcq = parsed.mcqAnswers;
          if (parsed.subjectiveAnswers && Object.keys(parsed.subjectiveAnswers).length) latestSubjective = parsed.subjectiveAnswers;
        }
      } catch (e) {
        console.error("LocalStorage fallback failed:", e);
      }
    }

    // Persist one final snapshot before submitting (so admin/recovery can see the data)
    try {
      const savedData = {
        mcqAnswers: latestMcq,
        subjectiveAnswers: latestSubjective,
        savedAt: new Date().toISOString(),
        autoSubmitted: true
      };
      localStorage.setItem(`examAnswers_${currentCandidate.id}`, JSON.stringify(savedData));
      console.log("Final save before auto-submit:", Object.keys(latestMcq).length, "MCQs,", Object.keys(latestSubjective).length, "subjective");
    } catch (e) {
      console.error("Final save failed:", e);
    }

    // CRITICAL: Also push a final draft to the SERVER before submitting.
    // If /submit fails (network / bulk congestion / timeout), the server still has the answers
    // and admin can recover via /admin/recover-draft/{id}.
    try {
      const mcqList = Object.entries(latestMcq).map(([id, answer]) => ({
        question_id: parseInt(id), answer
      }));
      const subjList = Object.entries(latestSubjective).map(([id, data]) => ({
        question_id: parseInt(id),
        selected_option: data.selectedOption,
        answer: data.answer || ""
      }));
      await axios.put(`${API}/candidate/auto-save/${currentCandidate.id}`, {
        candidate_id: currentCandidate.id,
        mcq_answers: mcqList,
        subjective_answers: subjList
      }, { timeout: 8000 });
      console.log("Final server draft pushed before auto-submit");
    } catch (e) {
      console.warn("Final server draft push failed (will still try /submit):", e?.message);
    }

    toast.info("Time's up! Auto-submitting your exam...", { duration: 3000 });

    setSubmitting(true);
    try {
      const mcqAnswersList = Object.entries(latestMcq).map(([id, answer]) => ({
        question_id: parseInt(id),
        answer
      }));

      const subjectiveAnswersList = Object.entries(latestSubjective).map(([id, data]) => ({
        question_id: parseInt(id),
        selected_option: data.selectedOption,
        answer: data.answer
      }));

      const response = await axios.post(`${API}/submit`, {
        candidate_id: currentCandidate.id,
        mcq_answers: mcqAnswersList,
        subjective_answers: subjectiveAnswersList
      });

      // Clear timer from localStorage
      localStorage.removeItem(`examTimer_${currentCandidate.id}`);

      setSubmissionResult(response.data);
      setShowSuccessDialog(true);
    } catch (error) {
      if (error.response?.data?.detail === "Exam already submitted") {
        setShowSuccessDialog(true);
      } else {
        toast.error("Auto-submission failed. Please try manually.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [candidate]);

  // Keep a ref to the latest handleAutoSubmit so the timer interval always calls the current version
  useEffect(() => {
    handleAutoSubmitRef.current = handleAutoSubmit;
  }, [handleAutoSubmit]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on time remaining
  const getTimerColor = () => {
    if (timeLeft <= 300) return "text-red-600 bg-red-50"; // Last 5 minutes
    if (timeLeft <= 900) return "text-amber-600 bg-amber-50"; // Last 15 minutes
    return "text-emerald-700 bg-emerald-50";
  };

  const fetchQuestions = async (candidateId) => {
    try {
      // Fetch shuffled questions specific to this candidate
      const response = await axios.get(`${API}/questions/${candidateId}`);
      setQuestions(response.data);
      
      // Initialize subjective answers based on actual question IDs
      const section3Questions = response.data.section3?.questions || [];
      const newSubjAnswers = {};
      section3Questions.forEach(q => {
        newSubjAnswers[q.id] = { selectedOption: 1, answer: "" };
      });
      // Only seed if we haven't already restored from localStorage (don't overwrite!)
      setSubjectiveAnswers(prev => {
        if (Object.keys(prev).length > 0 && Object.values(prev).some(v => v.answer && v.answer.length > 0)) {
          return prev; // Keep restored answers
        }
        subjectiveAnswersRef.current = newSubjAnswers;
        return newSubjAnswers;
      });
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentQuestions = () => {
    if (!questions) return [];
    return questions[currentSection]?.questions || [];
  };

  const handleMCQAnswer = (questionId, answer) => {
    // Update ref synchronously so timer/auto-submit always sees the latest answer
    mcqAnswersRef.current = { ...(mcqAnswersRef.current || {}), [questionId]: answer };
    setMcqAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubjectiveOptionChange = (questionId, option) => {
    const prevRef = subjectiveAnswersRef.current || {};
    const prevEntry = prevRef[questionId] || { selectedOption: 1, answer: "" };
    subjectiveAnswersRef.current = { ...prevRef, [questionId]: { ...prevEntry, selectedOption: option } };
    setSubjectiveAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], selectedOption: option }
    }));
  };

  const handleSubjectiveAnswer = (questionId, answer) => {
    const prevRef = subjectiveAnswersRef.current || {};
    const prevEntry = prevRef[questionId] || { selectedOption: 1, answer: "" };
    subjectiveAnswersRef.current = { ...prevRef, [questionId]: { ...prevEntry, answer } };
    setSubjectiveAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], answer }
    }));
  };

  // Keep refs in sync with the latest state so auto-submit always reads current values
  useEffect(() => {
    mcqAnswersRef.current = mcqAnswers;
  }, [mcqAnswers]);

  useEffect(() => {
    subjectiveAnswersRef.current = subjectiveAnswers;
  }, [subjectiveAnswers]);

  useEffect(() => {
    candidateRef.current = candidate;
  }, [candidate]);

  const getAnsweredCount = (section) => {
    if (!questions) return 0;
    const sectionQuestions = questions[section]?.questions || [];
    if (section === "section3") {
      return Object.values(subjectiveAnswers).filter(a => a.answer.trim().length > 0).length;
    }
    return sectionQuestions.filter(q => mcqAnswers[q.id]).length;
  };

  const getTotalAnswered = () => {
    const mcqCount = Object.keys(mcqAnswers).length;
    const subjectiveCount = Object.values(subjectiveAnswers).filter(a => a.answer.trim().length > 0).length;
    return mcqCount + subjectiveCount;
  };

  const handleSubmit = async () => {
    if (!candidate) return;

    setSubmitting(true);
    try {
      const mcqAnswersList = Object.entries(mcqAnswers).map(([id, answer]) => ({
        question_id: parseInt(id),
        answer
      }));

      const subjectiveAnswersList = Object.entries(subjectiveAnswers).map(([id, data]) => ({
        question_id: parseInt(id),
        selected_option: data.selectedOption,
        answer: data.answer
      }));

      // Push a final server draft right before the actual submit. Guarantees answers
      // are recoverable even if /submit itself fails.
      try {
        await axios.put(`${API}/candidate/auto-save/${candidate.id}`, {
          candidate_id: candidate.id,
          mcq_answers: mcqAnswersList,
          subjective_answers: subjectiveAnswersList
        }, { timeout: 8000 });
      } catch (e) {
        console.warn("Final draft before manual submit failed:", e?.message);
      }

      const response = await axios.post(`${API}/submit`, {
        candidate_id: candidate.id,
        mcq_answers: mcqAnswersList,
        subjective_answers: subjectiveAnswersList
      });

      // Clear timer and saved answers
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
      localStorage.removeItem(`examTimer_${candidate.id}`);
      localStorage.removeItem(`examAnswers_${candidate.id}`);

      setSubmissionResult(response.data);
      setShowSubmitDialog(false);
      setShowSuccessDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const navigateQuestion = (direction) => {
    const currentQuestions = getCurrentQuestions();
    const newIndex = currentQuestionIndex + direction;
    
    if (newIndex >= 0 && newIndex < currentQuestions.length) {
      setCurrentQuestionIndex(newIndex);
    } else if (direction > 0 && newIndex >= currentQuestions.length) {
      if (currentSection === "section1") {
        setCurrentSection("section2");
        setCurrentQuestionIndex(0);
      } else if (currentSection === "section2") {
        setCurrentSection("section3");
        setCurrentQuestionIndex(0);
      }
    } else if (direction < 0 && newIndex < 0) {
      if (currentSection === "section2") {
        setCurrentSection("section1");
        setCurrentQuestionIndex(questions.section1.questions.length - 1);
      } else if (currentSection === "section3") {
        setCurrentSection("section2");
        setCurrentQuestionIndex(questions.section2.questions.length - 1);
      }
    }
  };

  if (loading || !questions) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your personalized exam...</p>
        </div>
      </div>
    );
  }

  const currentQuestions = getCurrentQuestions();
  const currentQuestion = currentQuestions[currentQuestionIndex];
  const optionLetters = ["A", "B", "C", "D"];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="hidden lg:flex lg:flex-col w-80 bg-white border-r border-gray-200 exam-sidebar">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-emerald-50">
          <div className="flex items-center gap-3 mb-3">
            <div>
              <h1 className="text-lg font-bold text-emerald-900">WWAGS Exam</h1>
              <p className="text-xs text-gray-500">CRP-EP SET A</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User className="w-4 h-4" />
            <span className="truncate">{candidate?.name}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Reg: {candidate?.registration_number}</p>
          <div className="flex items-center gap-1 text-xs text-emerald-600 mt-2">
            <Shield className="w-3 h-3" />
            <span>Unique question order assigned</span>
          </div>
        </div>

        {/* Timer Display */}
        <div className={`p-4 border-b border-gray-200 ${getTimerColor()}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Time Remaining</span>
            </div>
            <span className="text-2xl font-bold font-mono" data-testid="exam-timer">
              {formatTime(timeLeft)}
            </span>
          </div>
          {timeLeft <= 300 && (
            <div className="flex items-center gap-1 mt-2 text-xs">
              <AlertTriangle className="w-3 h-3" />
              <span>Less than 5 minutes remaining!</span>
            </div>
          )}
          {/* Auto-save indicator */}
          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
            {autoSaving ? (
              <>
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <span>Saving...</span>
              </>
            ) : lastAutoSave ? (
              <>
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span>Auto-saved at {lastAutoSave.toLocaleTimeString()}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span>Auto-save enabled (every 30s)</span>
              </>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-emerald-700">{getTotalAnswered()} / 42</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-emerald-600 rounded-full transition-all duration-300"
              style={{ width: `${(getTotalAnswered() / 42) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Question Palette */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-6">
            {/* Section 1 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Section I ({getAnsweredCount("section1")}/{questions.section1.questions.length})
              </h3>
              <p className="text-xs text-emerald-600 mb-3">G.K, English, Reasoning • 25 Qs × 2 marks = 50 marks</p>
              <div className="grid grid-cols-6 gap-2">
                {questions.section1.questions.map((q, i) => (
                  <button
                    key={q.id}
                    onClick={() => { setCurrentSection("section1"); setCurrentQuestionIndex(i); }}
                    data-testid={`question-nav-${q.id}`}
                    className={`question-palette-btn ${
                      mcqAnswers[q.id] ? "answered" : "unanswered"
                    } ${currentSection === "section1" && currentQuestionIndex === i ? "current" : ""}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Section II ({getAnsweredCount("section2")}/{questions.section2.questions.length})
              </h3>
              <p className="text-xs text-amber-600 mb-3">Mathematics • 20 Qs × 2 marks = 40 marks (40% req.)</p>
              <div className="grid grid-cols-6 gap-2">
                {questions.section2.questions.map((q, i) => (
                  <button
                    key={q.id}
                    onClick={() => { setCurrentSection("section2"); setCurrentQuestionIndex(i); }}
                    data-testid={`question-nav-${q.id}`}
                    className={`question-palette-btn ${
                      mcqAnswers[q.id] ? "answered" : "unanswered"
                    } ${currentSection === "section2" && currentQuestionIndex === i ? "current" : ""}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">
                Section III ({getAnsweredCount("section3")}/{questions.section3.questions.length})
              </h3>
              <p className="text-xs text-teal-600 mb-3">Subjective • 2 Qs (Answer any 1) = 10 marks</p>
              <div className="grid grid-cols-6 gap-2">
                {questions.section3.questions.map((q, i) => (
                  <button
                    key={q.id}
                    onClick={() => { setCurrentSection("section3"); setCurrentQuestionIndex(i); }}
                    data-testid={`question-nav-${q.id}`}
                    className={`question-palette-btn ${
                      subjectiveAnswers[q.id]?.answer?.trim() ? "answered" : "unanswered"
                    } ${currentSection === "section3" && currentQuestionIndex === i ? "current" : ""}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-600 mb-2">Legend</p>
            <div className="flex gap-4 text-xs">
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-emerald-600"></span>
                <span>Answered</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-white border border-gray-300"></span>
                <span>Not Answered</span>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Submit Button */}
        <div className="p-4 border-t border-gray-200">
          <Button 
            onClick={() => setShowSubmitDialog(true)}
            className="w-full bg-emerald-700 hover:bg-emerald-800"
            data-testid="submit-exam-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Submit Exam
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-emerald-900">WWAGS</span>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-1 px-2 py-1 rounded ${getTimerColor()}`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold" data-testid="mobile-exam-timer">
                  {formatTime(timeLeft)}
                </span>
              </div>
              <span className="text-sm text-gray-600">{getTotalAnswered()}/42</span>
            </div>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="bg-white border-b border-gray-200 px-4 pt-4">
          <Tabs value={currentSection} onValueChange={(v) => { setCurrentSection(v); setCurrentQuestionIndex(0); }}>
            <TabsList className="grid grid-cols-3 w-full max-w-lg">
              <TabsTrigger value="section1" data-testid="section1-tab" className="text-xs sm:text-sm">
                Section I
              </TabsTrigger>
              <TabsTrigger value="section2" data-testid="section2-tab" className="text-xs sm:text-sm">
                Section II
              </TabsTrigger>
              <TabsTrigger value="section3" data-testid="section3-tab" className="text-xs sm:text-sm">
                Section III
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="py-2">
            <p className="text-sm text-gray-500">{questions[currentSection]?.title}</p>
            {questions[currentSection]?.title_as && (
              <p className="text-xs text-gray-400">{questions[currentSection]?.title_as}</p>
            )}
            {/* Section marks info */}
            <p className="text-xs mt-1 font-medium">
              {currentSection === "section1" && <span className="text-emerald-600">25 Questions × 2 marks = 50 marks</span>}
              {currentSection === "section2" && <span className="text-amber-600">20 Questions × 2 marks = 40 marks (40% required to qualify)</span>}
              {currentSection === "section3" && <span className="text-teal-600">2 Questions (Answer any ONE) = 10 marks</span>}
            </p>
          </div>
        </div>

        {/* Question Content - extra bottom padding on mobile so last option clears the fixed nav bar */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-[260px] sm:pb-6 lg:pb-8">
          {currentSection !== "section3" ? (
            /* MCQ Question */
            <Card className="max-w-3xl mx-auto border-l-4 border-l-emerald-600 shadow-md fade-in">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-emerald-700">
                    Question {currentQuestionIndex + 1}
                  </span>
                  <span className="text-xs text-gray-500">
                    {currentQuestionIndex + 1} of {currentQuestions.length}
                  </span>
                </div>
                <CardTitle className="text-lg text-gray-900 leading-relaxed">
                  {currentQuestion?.text}
                </CardTitle>
                {currentQuestion?.text_as && (
                  <p className="text-base text-gray-600 mt-2">
                    {currentQuestion?.text_as}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={mcqAnswers[currentQuestion?.id] || ""}
                  onValueChange={(value) => handleMCQAnswer(currentQuestion?.id, value)}
                >
                  <div className="space-y-3">
                    {currentQuestion?.options?.map((option, i) => (
                      <div
                        key={i}
                        className={`option-card ${
                          mcqAnswers[currentQuestion?.id] === optionLetters[i] ? "selected" : ""
                        }`}
                        onClick={() => handleMCQAnswer(currentQuestion?.id, optionLetters[i])}
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem 
                            value={optionLetters[i]} 
                            id={`option-${i}`}
                            data-testid={`option-${currentQuestion?.id}-${optionLetters[i]}`}
                          />
                          <Label htmlFor={`option-${i}`} className="flex-1 cursor-pointer">
                            <span className="font-medium text-emerald-700 mr-2">{optionLetters[i]}.</span>
                            {option}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          ) : (
            /* Subjective Question */
            <Card className="max-w-4xl mx-auto border-l-4 border-l-teal-600 shadow-md fade-in">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-teal-700">
                    Question {currentQuestionIndex + 1} (Answer any ONE - 10 Marks)
                  </span>
                  <span className="text-xs text-gray-500">
                    {currentQuestionIndex + 1} of {currentQuestions.length}
                  </span>
                </div>
                
                {/* Option 1 */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all mb-4 ${
                    subjectiveAnswers[currentQuestion?.id]?.selectedOption === 1 
                      ? "border-teal-600 bg-teal-50" 
                      : "border-gray-200 hover:border-teal-400"
                  }`}
                  onClick={() => handleSubjectiveOptionChange(currentQuestion?.id, 1)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                      subjectiveAnswers[currentQuestion?.id]?.selectedOption === 1 
                        ? "border-teal-600 bg-teal-600" 
                        : "border-gray-400"
                    }`}>
                      {subjectiveAnswers[currentQuestion?.id]?.selectedOption === 1 && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{currentQuestion?.text}</p>
                      {currentQuestion?.text_as && (
                        <p className="text-sm text-gray-600 mt-1">{currentQuestion?.text_as}</p>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-center text-gray-500 font-medium my-2">OR</p>

                {/* Option 2 */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    subjectiveAnswers[currentQuestion?.id]?.selectedOption === 2 
                      ? "border-teal-600 bg-teal-50" 
                      : "border-gray-200 hover:border-teal-400"
                  }`}
                  onClick={() => handleSubjectiveOptionChange(currentQuestion?.id, 2)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                      subjectiveAnswers[currentQuestion?.id]?.selectedOption === 2 
                        ? "border-teal-600 bg-teal-600" 
                        : "border-gray-400"
                    }`}>
                      {subjectiveAnswers[currentQuestion?.id]?.selectedOption === 2 && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{currentQuestion?.alt_text}</p>
                      {currentQuestion?.alt_text_as && (
                        <p className="text-sm text-gray-600 mt-1">{currentQuestion?.alt_text_as}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Your Answer (Minimum 250 words)
                </Label>
                <Textarea
                  placeholder="Write your answer here..."
                  value={subjectiveAnswers[currentQuestion?.id]?.answer || ""}
                  onChange={(e) => handleSubjectiveAnswer(currentQuestion?.id, e.target.value)}
                  className="min-h-[250px] resize-y"
                  data-testid={`subjective-answer-${currentQuestion?.id}`}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Word count: {subjectiveAnswers[currentQuestion?.id]?.answer?.trim().split(/\s+/).filter(Boolean).length || 0}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Navigation - fixed at viewport bottom on mobile, sticky on desktop */}
        <div className="bg-white border-t border-gray-200 p-3 sm:p-4 fixed sm:sticky bottom-0 left-0 right-0 z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          {/* Mobile layout - stacked buttons */}
          <div className="sm:hidden max-w-3xl mx-auto">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                onClick={() => navigateQuestion(-1)}
                disabled={currentSection === "section1" && currentQuestionIndex === 0}
                data-testid="prev-question-btn"
                className="flex-1 h-12"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => navigateQuestion(1)}
                disabled={currentSection === "section3" && currentQuestionIndex === currentQuestions.length - 1}
                data-testid="next-question-btn"
                className="flex-1 h-12"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Generous gap + divider to prevent accidental Submit taps on mobile */}
            <div className="mt-5 pt-3 border-t border-dashed border-gray-300">
              <Button 
                onClick={() => setShowSubmitDialog(true)}
                variant="outline"
                className="w-full h-11 border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-50"
                data-testid="mobile-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Submit Exam
              </Button>
              <p className="text-[10px] text-center text-gray-400 mt-1">
                Tap only when you have finished all questions
              </p>
            </div>
          </div>
          
          {/* Desktop layout - horizontal */}
          <div className="hidden sm:flex max-w-3xl mx-auto items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigateQuestion(-1)}
              disabled={currentSection === "section1" && currentQuestionIndex === 0}
              data-testid="prev-question-btn-desktop"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>

            <Button
              variant="outline"
              onClick={() => navigateQuestion(1)}
              disabled={currentSection === "section3" && currentQuestionIndex === currentQuestions.length - 1}
              data-testid="next-question-btn-desktop"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Examination?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>You have answered {getTotalAnswered()} out of 42 questions.</p>
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p>Section I: {getAnsweredCount("section1")}/{questions.section1.questions.length} answered</p>
                <p>Section II: {getAnsweredCount("section2")}/{questions.section2.questions.length} answered</p>
                <p>Section III: {getAnsweredCount("section3")}/{questions.section3.questions.length} answered</p>
              </div>
              <p className="text-amber-600 font-medium">
                Once submitted, you cannot modify your answers.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Review Answers</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="confirm-submit-btn"
            >
              {submitting ? "Submitting..." : "Confirm Submit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <DialogTitle className="text-center text-2xl">Exam Submitted Successfully!</DialogTitle>
            <DialogDescription className="text-center space-y-4">
              <p>Your exam has been submitted successfully.</p>
              <div className="bg-emerald-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Your answers have been recorded. Results will be announced after evaluation by the administrator.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={() => { localStorage.removeItem("candidate"); navigate("/"); }}
              className="w-full bg-emerald-700 hover:bg-emerald-800"
              data-testid="go-home-btn"
            >
              Back to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
